import { Component } from '@theme/component';
import {
  fetchConfig,
  debounce,
  onAnimationEnd,
  prefersReducedMotion,
  resetShimmer,
  startViewTransition,
} from '@theme/utilities';
import { morphSection, sectionRenderer } from '@theme/section-renderer';
import {
  ThemeEvents,
  CartUpdateEvent,
  QuantitySelectorUpdateEvent,
  CartAddEvent,
  DiscountUpdateEvent,
} from '@theme/events';
import { cartPerformance } from '@theme/performance';

/** @typedef {import('./utilities').TextComponent} TextComponent */

/**
 * A custom element that displays a cart items component.
 *
 * @typedef {object} Refs
 * @property {HTMLElement[]} quantitySelectors - The quantity selector elements.
 * @property {HTMLTableRowElement[]} cartItemRows - The cart item rows.
 * @property {TextComponent} cartTotal - The cart total.
 *
 * @extends {Component<Refs>}
 */
class CartItemsComponent extends Component {
  #debouncedOnChange = debounce(this.#onQuantityChange, 300).bind(this);

  connectedCallback() {
    super.connectedCallback();

    this.addEventListener('click', this.#handleClearAllClick);
    document.addEventListener(ThemeEvents.cartUpdate, this.#handleCartUpdate);
    document.addEventListener(ThemeEvents.discountUpdate, this.handleDiscountUpdate);
    document.addEventListener(ThemeEvents.quantitySelectorUpdate, this.#debouncedOnChange);
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    this.removeEventListener('click', this.#handleClearAllClick);
    document.removeEventListener(ThemeEvents.cartUpdate, this.#handleCartUpdate);
    document.removeEventListener(ThemeEvents.discountUpdate, this.handleDiscountUpdate);
    document.removeEventListener(ThemeEvents.quantitySelectorUpdate, this.#debouncedOnChange);
  }

  /**
   * Handles clear-all buttons inside either the drawer or main cart page.
   * @param {MouseEvent} event - The click event.
   */
  #handleClearAllClick = async (event) => {
    if (!(event.target instanceof Element)) return;

    const button = event.target.closest('[data-cart-clear-all]');
    if (!(button instanceof HTMLButtonElement) || !this.contains(button) || button.disabled) return;

    button.disabled = true;

    const sectionsToUpdate = new Set([this.sectionId]);
    document.querySelectorAll('cart-items-component[data-section-id]').forEach((component) => {
      if (component instanceof HTMLElement && component.dataset.sectionId) {
        sectionsToUpdate.add(component.dataset.sectionId);
      }
    });

    const body = JSON.stringify({
      sections: Array.from(sectionsToUpdate).join(','),
      sections_url: window.location.pathname,
    });

    try {
      const response = await fetch(Theme.routes.cart_clear_url || '/cart/clear.js', fetchConfig('json', { body }));
      const cart = await response.json();

      if (!response.ok || cart.errors) throw new Error(cart.errors || 'Cart clear failed');

      const renderedEmptyState = this.#renderEmptyCart();
      const cartUpdateEvent = new CartUpdateEvent(cart, 'cart-clear-all', {
        itemCount: 0,
        source: 'cart-clear-all',
        sections: cart.sections,
      });

      if (renderedEmptyState) {
        this.dispatchEvent(cartUpdateEvent);
      } else {
        document.dispatchEvent(cartUpdateEvent);
      }

      this.#resetProductTabsCards();
    } catch (error) {
      console.error(error);
      button.disabled = false;
    }
  };

  /**
   * Handles QuantitySelectorUpdateEvent change event.
   * @param {QuantitySelectorUpdateEvent} event - The event.
   */
  #onQuantityChange(event) {
    if (!(event.target instanceof Node) || !this.contains(event.target)) return;

    const { quantity, cartLine: line } = event.detail;

    // Cart items require a line number
    if (!line) return;

    if (quantity === 0) {
      return this.onLineItemRemove(line);
    }

    this.updateQuantity({
      line,
      quantity,
      action: 'change',
    });
    const lineItemRow = this.refs.cartItemRows[line - 1];

    if (!lineItemRow) return;

    const textComponent = /** @type {TextComponent | undefined} */ (lineItemRow.querySelector('text-component'));
    textComponent?.shimmer();
  }

  /**
   * Handles the line item removal.
   * @param {number} line - The line item index.
   */
  onLineItemRemove(line) {
    this.updateQuantity({
      line,
      quantity: 0,
      action: 'clear',
    });

    const cartItemRowToRemove = this.refs.cartItemRows[line - 1];

    if (!cartItemRowToRemove) return;

    const rowsToRemove = [
      cartItemRowToRemove,
      // Get all nested lines of the row to remove
      ...this.refs.cartItemRows.filter((row) => row.dataset.parentKey === cartItemRowToRemove.dataset.key),
    ];

    // If the cart item row is the last row, optimistically trigger the cart empty state
    const isEmptyCart = rowsToRemove.length == this.refs.cartItemRows.length;

    // Delegate to #renderEmptyCart rather than inlining the template swap. The inline copy
    // rendered the empty markup but never flagged the dialog, so emptying the cart with a
    // line's own trash icon left it without cart-drawer--empty and with aria-labelledby
    // still pointing at the populated heading — the empty state drawn in populated styling.
    // The clear-all path always went through #renderEmptyCart and was unaffected.
    // Returns false when there is no template, in which case fall through to the row
    // removal animation below, as before.
    if (isEmptyCart && this.#renderEmptyCart()) {
      return;
    }

    // Add class to the row to trigger the animation
    rowsToRemove.forEach((row) => {
      const remove = () => row.remove();

      if (prefersReducedMotion()) return remove();

      row.style.setProperty('--row-height', `${row.clientHeight}px`);
      row.classList.add('removing');

      // Remove the row after the animation ends
      onAnimationEnd(row, remove);
    });
  }

  /**
   * Updates the quantity.
   * @param {Object} config - The config.
   * @param {number} config.line - The line.
   * @param {number} config.quantity - The quantity.
   * @param {string} config.action - The action.
   */
  updateQuantity(config) {
    const cartPerformaceUpdateMarker = cartPerformance.createStartingMarker(`${config.action}:user-action`);

    this.#disableCartItems();

    const { line, quantity } = config;
    const { cartTotal } = this.refs;

    const cartItemsComponents = document.querySelectorAll('cart-items-component');
    const sectionsToUpdate = new Set([this.sectionId]);
    cartItemsComponents.forEach((item) => {
      if (item instanceof HTMLElement && item.dataset.sectionId) {
        sectionsToUpdate.add(item.dataset.sectionId);
      }
    });

    const body = JSON.stringify({
      line: line,
      quantity: quantity,
      sections: Array.from(sectionsToUpdate).join(','),
      sections_url: window.location.pathname,
    });

    cartTotal?.shimmer();

    fetch(`${Theme.routes.cart_change_url}`, fetchConfig('json', { body }))
      .then(async (response) => {
        // Deliberately not gated on response.ok: the cart API answers ordinary errors —
        // exceeding available stock, most commonly — with HTTP 422 and a JSON body that
        // the errors branch below turns into an inline message. Rejecting on status would
        // swallow those. Only a body that is not JSON at all (error page, bot challenge)
        // is a hard failure, which is what produced the opaque "Unexpected token <".
        const responseText = await response.text();

        try {
          return JSON.parse(responseText);
        } catch {
          throw new Error(`Cart update failed: HTTP ${response.status}, response was not JSON`);
        }
      })
      .then((parsedResponseText) => {
        resetShimmer(this);

        if (parsedResponseText.errors) {
          this.#handleCartError(line, parsedResponseText);
          return;
        }

        const newSectionHTML = new DOMParser().parseFromString(
          parsedResponseText.sections[this.sectionId],
          'text/html'
        );

        // Grab the new cart item count from a hidden element
        const newCartHiddenItemCount = newSectionHTML.querySelector('[ref="cartItemCount"]')?.textContent;
        const newCartItemCount = newCartHiddenItemCount ? parseInt(newCartHiddenItemCount, 10) : 0;

        // Update data-cart-quantity for all matching variants
        this.#updateQuantitySelectors(parsedResponseText);

        this.dispatchEvent(
          new CartUpdateEvent(parsedResponseText, this.sectionId, {
            itemCount: newCartItemCount,
            source: 'cart-items-component',
            sections: parsedResponseText.sections,
          })
        );

        morphSection(this.sectionId, parsedResponseText.sections[this.sectionId], { mode: this.isDrawer ? 'hydration' : 'full' });

        this.#updateCartQuantitySelectorButtonStates();
      })
      .catch((error) => {
        // Stop the shimmer here too — it is only cleared on the success path, so any
        // failure used to leave the total shimmering indefinitely.
        resetShimmer(this);
        console.error(error);
      })
      .finally(() => {
        this.#enableCartItems();
        cartPerformance.measureFromMarker(cartPerformaceUpdateMarker);
      });
  }

  /**
   * Handles the discount update.
   * @param {DiscountUpdateEvent} event - The event.
   */
  handleDiscountUpdate = (event) => {
    this.#handleCartUpdate(event);
  };

  /**
   * Handles the cart error.
   * @param {number} line - The line.
   * @param {Object} parsedResponseText - The parsed response text.
   * @param {string} parsedResponseText.errors - The errors.
   */
  #handleCartError = (line, parsedResponseText) => {
    const quantitySelector = this.refs.quantitySelectors[line - 1];
    const quantityInput = quantitySelector?.querySelector('input');

    if (!quantityInput) throw new Error('Quantity input not found');

    quantityInput.value = quantityInput.defaultValue;

    const cartItemError = this.refs[`cartItemError-${line}`];
    const cartItemErrorContainer = this.refs[`cartItemErrorContainer-${line}`];

    if (!(cartItemError instanceof HTMLElement)) throw new Error('Cart item error not found');
    if (!(cartItemErrorContainer instanceof HTMLElement)) throw new Error('Cart item error container not found');

    cartItemError.textContent = parsedResponseText.errors;
    cartItemErrorContainer.classList.remove('hidden');
  };

  /**
   * Handles the cart update.
   *
   * @param {DiscountUpdateEvent | CartUpdateEvent | CartAddEvent} event
   */
  #handleCartUpdate = (event) => {
    // Drawer must morph in hydration mode (matches updateQuantity above).
    // Full mode rewrites the drawer's structural wrapper and corrupts open
    // state / line item layout — visible as a half-empty drawer when an
    // external listener (qb-cart-upsell) dispatches cart:update with sections.
    const mode = this.isDrawer ? 'hydration' : 'full';

    if (event instanceof DiscountUpdateEvent) {
      sectionRenderer.renderSection(this.sectionId, { cache: false, mode });
      return;
    }
    if (event.detail?.data?.optimistic) return;
    if (event.target === this) return;

    const cartItemsHtml = event.detail.data.sections?.[this.sectionId];
    if (cartItemsHtml) {
      // Chained rather than awaited so the button-state call below keeps its
      // existing ordering; the empty state can only be judged once the new
      // markup is in place.
      morphSection(this.sectionId, cartItemsHtml, { mode }).then(() => this.#clearEmptyCartState());

      // Update button states for all cart quantity selectors after morph
      this.#updateCartQuantitySelectorButtonStates();
    } else {
      sectionRenderer
        .renderSection(this.sectionId, { cache: false, mode })
        .then(() => this.#clearEmptyCartState());
    }
  };

  /**
   * Disables the cart items.
   */
  #disableCartItems() {
    this.classList.add('cart-items-disabled');
  }

  /**
   * Enables the cart items.
   */
  #enableCartItems() {
    this.classList.remove('cart-items-disabled');
  }

  /**
   * Updates quantity selectors for all matching variants in the cart.
   * @param {Object} updatedCart - The updated cart object.
   * @param {Array<{variant_id: number, quantity: number}>} [updatedCart.items] - The cart items.
   */
  #updateQuantitySelectors(updatedCart) {
    if (!updatedCart.items) return;

    for (const item of updatedCart.items) {
      const variantId = item.variant_id.toString();
      const selectors = document.querySelectorAll(`quantity-selector-component[data-variant-id="${variantId}"]`);

      for (const selector of selectors) {
        const input = selector.querySelector('input[data-cart-quantity]');
        if (!input) continue;

        input.setAttribute('data-cart-quantity', item.quantity.toString());

        // Update the quantity selector's internal state
        if ('updateCartQuantity' in selector && typeof selector.updateCartQuantity === 'function') {
          selector.updateCartQuantity();
        }
      }
    }
  }

  /**
   * Updates button states for all cart quantity selector components.
   */
  #updateCartQuantitySelectorButtonStates() {
    for (const selector of document.querySelectorAll('cart-quantity-selector-component')) {
      /** @type {any} */ (selector).updateButtonStates?.();
    }
  }

  /**
   * Renders the matching empty cart state for this cart surface.
   * @returns {boolean} Whether the empty template was rendered directly.
   */
  #renderEmptyCart() {
    const template = this.#getEmptyCartTemplate();
    if (!(template instanceof HTMLTemplateElement)) return false;

    startViewTransition(
      () => {
        const clone = document.importNode(template.content, true);
        this.replaceChildren(clone);

        if (this.isDrawer) {
          const dialog = this.closest('.cart-drawer__dialog');
          dialog?.classList.add('cart-drawer--empty');
          dialog?.setAttribute('aria-labelledby', 'cart-drawer-heading-empty');
        }
      },
      [this.isDrawer ? 'empty-cart-drawer' : 'empty-cart-page']
    );

    return true;
  }

  /**
   * Clears the drawer's empty state once the cart has items again.
   *
   * Counterpart to #renderEmptyCart. The `cart-drawer--empty` class lives on the
   * <dialog>, which sits outside this component, so morphing the contents never
   * clears it — it survived until the next server render of the header. A cart
   * refilled over AJAX therefore kept the empty-state styling, most visibly
   * `.cart-drawer--empty .cart-drawer__content { text-align: center }`, which
   * centred every line item, the totals and the checkout button.
   *
   * `.cart-form` only renders on the populated branch of cart-products.liquid, and
   * is the same signal the stylesheet keys its own has-items rules on.
   */
  #clearEmptyCartState() {
    if (!this.isDrawer) return;

    const dialog = this.closest('.cart-drawer__dialog');
    if (!dialog?.classList.contains('cart-drawer--empty')) return;
    if (!this.querySelector('.cart-form')) return;

    dialog.classList.remove('cart-drawer--empty');
    dialog.setAttribute('aria-labelledby', 'cart-drawer-heading');
  }

  /**
   * Resets product tab card quantity controls after the cart is emptied.
   */
  #resetProductTabsCards() {
    document.querySelectorAll('[data-ptc-card].ptc--qty-open').forEach((card) => {
      card.classList.remove('ptc--qty-open');
      const display = card.querySelector('[data-qty-value]');
      const input = card.querySelector('[data-qty-input]');
      if (display) display.textContent = '1';
      if (input instanceof HTMLInputElement) input.value = '1';
    });
  }

  /**
   * Gets the empty-cart template scoped to the current cart surface.
   * @returns {HTMLTemplateElement | null}
   */
  #getEmptyCartTemplate() {
    const scopedTemplate = this.querySelector('template[data-empty-cart-template]');
    if (scopedTemplate instanceof HTMLTemplateElement) return scopedTemplate;

    const templateId = this.isDrawer ? 'empty-cart-template-drawer' : 'empty-cart-template-page';
    const surfaceTemplate = document.getElementById(templateId);
    if (surfaceTemplate instanceof HTMLTemplateElement) return surfaceTemplate;

    const legacyTemplate = document.getElementById('empty-cart-template');
    return legacyTemplate instanceof HTMLTemplateElement ? legacyTemplate : null;
  }

  /**
   * Gets the section id.
   * @returns {string} The section id.
   */
  get sectionId() {
    const { sectionId } = this.dataset;

    if (!sectionId) throw new Error('Section id missing');

    return sectionId;
  }

  /**
   * @returns {boolean} Whether the component is a drawer.
   */
  get isDrawer() {
    return this.dataset.drawer !== undefined;
  }
}

if (!customElements.get('cart-items-component')) {
  customElements.define('cart-items-component', CartItemsComponent);
}
